import { FC, useCallback } from 'react'
import cn from 'classnames'
import ActionButton from 'backoffice/components/DropRequests/ActionButton'
import { useActionModalContext } from 'backoffice/components/DropRequests/Modals/ConfirmationModal/ActionModalContext'
import { Actions, Status } from './types/types'
import { Petition } from 'backoffice/components/DropRequests/usePetitionsQuery'

// Assets
import { ReactComponent as IconReject } from 'images/icons/icon-transparent-reject.svg'
import { ReactComponent as IconApprove } from 'images/icons/icon-transparent-approve.svg'
import { ReactComponent as IconFlag } from 'images/icons/icon-transparent-flag.svg'
import styles from './CardButtons.module.scss'

const ICON_SIZE = 42

type CardButtonsProps = {
  petition: Petition
}

type Button = {
  action: Actions
  icon: JSX.Element
  tooltipText: string
  styles: string
}

export const CardActionsPerStatus = [
  { status: Status.FLAGGED, actions: [Actions.APPROVE, Actions.REJECT] },
  { status: Status.PENDING, actions: [Actions.APPROVE, Actions.FLAG, Actions.REJECT] },
]

const CardButtons: FC<CardButtonsProps> = ({ petition }) => {
  const { setActionModal } = useActionModalContext()

  const getActionDataFromStatus = (status: Actions) => {
    let actionMenuData: Button | undefined

    switch (status) {
      case Actions.APPROVE:
        actionMenuData = {
          action: Actions.APPROVE,
          icon: <IconApprove width={ICON_SIZE} height={ICON_SIZE} />,
          tooltipText: 'Approve',
          styles: cn(styles.button, styles.actionApprove),
        }
        break
      case Actions.FLAG:
        actionMenuData = {
          action: Actions.FLAG,
          icon: <IconFlag width={ICON_SIZE} height={ICON_SIZE} />,
          tooltipText: 'Flag',
          styles: cn(styles.button, styles.actionFlag),
        }
        break
      case Actions.REJECT:
        actionMenuData = {
          action: Actions.REJECT,
          icon: <IconReject width={ICON_SIZE} height={ICON_SIZE} />,
          tooltipText: 'Reject',
          styles: cn(styles.button, styles.actionReject),
        }
        break
    }

    return actionMenuData
  }

  const availableActions = CardActionsPerStatus.find((cardAction) => cardAction.status === petition.status)?.actions
  const cardButtons = availableActions?.map((action) => getActionDataFromStatus(action))

  const handleCick = useCallback(
    /* @ts-ignore TODO: remove ignore TS validation */
    (action) => {
      setActionModal({ action, petition })
    },
    [petition, setActionModal],
  )

  if (!cardButtons) {
    return null
  }

  return (
    <div className={styles.buttons} data-test-id={`card_buttons_container`}>
      {cardButtons?.map((button: Button | undefined) => {
        if (!button) {
          return null
        }

        return (
          <div
            className={styles.buttonWrapper}
            key={button.action}
            aria-label={button.tooltipText}
            data-cooltipz-dir="top"
            data-test-id={`button_container_${button.action}`}
          >
            {/* @ts-ignore TODO: remove ignore TS validation */}
            <ActionButton id={button.action} className={button.styles} onClick={() => handleCick(button.action)}>
              {button.icon}
            </ActionButton>
          </div>
        )
      })}
    </div>
  )
}

export default CardButtons
